import React, { useState } from "react";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { DatasetDropdown } from "components/basic/dropdowns/dataset/DatasetDropdown";
import { DashboardEditorProps } from "components/ComponentType";
import { IDateRange } from "components/dashboard/product-template-table/utils/interfaces";
import { DatasetLoadingConf } from "components/dashboard/charts/interfaces";
import LoadConfigInputs, {
  DefaultDatasetLoadingConf,
} from "components/dashboard/charts/LoadConfigInputs";
import { InputSwitch } from "primereact/inputswitch";
import { Button } from "primereact/button";
import { T } from "services/lang/LanguageService";

enum Axis {
  X_AXIS = "xAxis",
  Y_AXIS = "yAxis",
}

enum ColorKey {
  MIN_COLOR = "minColor",
  MAX_COLOR = "maxColor",
}

export const defaultMinColor = "#daedf2";
export const defaultMaxColor = "#3376b0";

export interface IGridHeatmapEditorProps extends IDateRange {
  xAxis: {
    title: string;
    identifier: string;
    minValue?: number;
    maxValue?: number;
  };
  yAxis: {
    title: string;
    identifier: string;
    minColor?: string;
    maxColor?: string;
    minValue?: number;
    maxValue?: number;
  };
  yAxisSecondary?: {
    title: string;
    identifier: string;
    minColor?: string;
    maxColor?: string;
    minValue?: number;
    maxValue?: number;
  };
  isLoadConfig: boolean;
  chartTitle?: string;
  axisStepSize?: number;
  minColor?: string;
  maxColor?: string;
  loadConf?: DatasetLoadingConf;
}

const GridHeatmapEditor = ({
  config,
  onConfigChange,
}: DashboardEditorProps<IGridHeatmapEditorProps>) => {
  const [currentConfig, setCurrentConfig] = useState<IGridHeatmapEditorProps>(
    config || {
      xAxis: { title: "", identifier: "" },
      yAxis: { title: "", identifier: "" },
      isLoadConfig: false,
      axisStepSize: 100,
      minColor: defaultMinColor,
      maxColor: defaultMaxColor,
      loadConf: DefaultDatasetLoadingConf,
    }
  );

  const handleDatasetChange = (e: any, axis: Axis, yAxisNr?: number): void => {
    const newConfig = { ...currentConfig };

    if (axis === Axis.Y_AXIS && yAxisNr === 2) {
      newConfig.yAxisSecondary = {
        ...newConfig.yAxisSecondary,
        identifier: e,
        title: newConfig.yAxisSecondary?.title || "",
      };
    } else {
      newConfig[axis] = { ...newConfig[axis], identifier: e };
    }

    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  const handleAxisTitleChange = (e: any, axis: Axis, yAxisNr?: number): void => {
    const newConfig = { ...currentConfig };

    if (axis === Axis.Y_AXIS && yAxisNr === 2) {
      newConfig.yAxisSecondary = {
        ...newConfig.yAxisSecondary,
        identifier: newConfig.yAxisSecondary?.identifier || "",
        title: e.target.value,
      };
    } else {
      newConfig[axis] = { ...newConfig[axis], title: e.target.value };
    }

    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  const handleAxisSizeChange = (e: any): void => {
    const newConfig = {
      ...currentConfig,
      axisStepSize: e.value,
    };
    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  const handleColorChange = (colorKey: ColorKey, colorValue: string, yAxisNr?: number): void => {
    const newAxis = { ...currentConfig };
    if (yAxisNr === 2) {
      newAxis.yAxisSecondary = {
        ...currentConfig.yAxisSecondary,
        title: currentConfig.yAxisSecondary?.title || "",
        identifier: currentConfig.yAxisSecondary?.identifier || "",
        [colorKey]: colorValue,
      };
    } else {
      newAxis.yAxis = {
        ...currentConfig.yAxis,
        title: currentConfig.yAxis?.title || "",
        identifier: currentConfig.yAxis?.identifier || "",
        [colorKey]: colorValue,
      };
    }

    const newConfig = {
      ...currentConfig,
      ...newAxis,
    };
    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  const handleResetColor = (colorKey: ColorKey, yAxisNr?: number): void => {
    const newConfig = {
      ...currentConfig,
    };
    if (yAxisNr === 2) {
      newConfig.yAxisSecondary = {
        ...currentConfig.yAxisSecondary,
        title: currentConfig.yAxisSecondary?.title || "",
        identifier: currentConfig.yAxisSecondary?.identifier || "",
        [colorKey]: undefined,
      };
    } else {
      newConfig.yAxis = {
        ...currentConfig.yAxis,
        title: currentConfig.yAxis?.title || "",
        identifier: currentConfig.yAxis?.identifier || "",
        [colorKey]: undefined,
      };
    }
    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  const handleLoadConfChange = (updatedLoadConf: DatasetLoadingConf) => {
    const newConfig = {
      ...currentConfig,
      loadConf: updatedLoadConf,
    };
    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  const handleChartTitleChange = (e: any): void => {
    const newConfig = {
      ...currentConfig,
      chartTitle: e.target.value,
    };
    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  const handleLoadConfStateChange = (e: any): void => {
    const newConfig = {
      ...currentConfig,
      isLoadConfig: e.value,
      loadConf: e.value ? DefaultDatasetLoadingConf : undefined,
    };
    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  const handleAddSecondaryAxis = () => {
    const newConfig = {
      ...currentConfig,
      yAxisSecondary: {
        title: "",
        identifier: "",
      },
    };
    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  const handleRemoveSecondaryAxis = () => {
    const newConfig = {
      ...currentConfig,
      yAxisSecondary: undefined,
    };
    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  const handleAxisMinMaxValueChange = (
    e: any,
    axisParameter: "minValue" | "maxValue",
    xAxis?: boolean,
    yAxisNr?: number
  ): void => {
    const newConfig = { ...currentConfig };
    if (xAxis) {
      newConfig.xAxis = {
        ...newConfig.xAxis,
        [axisParameter]: e.target.value,
      };
    }
    if (!xAxis && yAxisNr === 2) {
      newConfig.yAxisSecondary = {
        title: newConfig.yAxisSecondary?.title || "",
        identifier: newConfig.yAxisSecondary?.identifier || "",
        ...newConfig.yAxisSecondary,
        [axisParameter]: e.target.value,
      };
    } else if (!xAxis) {
      newConfig.yAxis = {
        ...newConfig.yAxis,
        [axisParameter]: e.target.value,
      };
    }

    setCurrentConfig(newConfig);
    onConfigChange(newConfig);
  };

  return (
    <div>
      <div className="flex flex-row gap-2 justify-content-between mb-3">
        <div className="flex flex-row gap-3">
          <div className="flex flex-column">
            <label htmlFor="chartTitle">{T("word.chart-title")}</label>
            <InputText
              id="chartTitle"
              type="text"
              value={currentConfig.chartTitle || ""}
              onChange={handleChartTitleChange}
            />
          </div>
          <div className="flex flex-column">
            <label htmlFor="axisSize">{T("label.grid-heatmap.axis-step-size-min-default")}</label>
            <InputNumber
              id="axisSize"
              maxFractionDigits={2}
              value={currentConfig.axisStepSize || 1}
              onValueChange={handleAxisSizeChange}
            />
          </div>
          <div className="flex flex-column">
            <label htmlFor="loadConf">{T("load-conf.load-config")}</label>
            <InputSwitch
              id="loadConf"
              checked={currentConfig.isLoadConfig}
              onChange={handleLoadConfStateChange}
            />
          </div>
        </div>
      </div>
      <div className="col-12 mb-3 border-1 border-round border-300">
        <div>{T("word.x-axis")}</div>
        <div className="col-12">
          <label htmlFor="dataset">{T("word.dataset")}</label>
          <DatasetDropdown
            value={currentConfig.xAxis?.identifier ?? ""}
            onChange={e => handleDatasetChange(e, Axis.X_AXIS)}
          />
        </div>
        <div className="flex flex-row gap-2">
          <div className="col-4 flex flex-column">
            <label htmlFor="title">{T("word.title")}</label>
            <InputText
              id="title"
              type="text"
              value={currentConfig.xAxis?.title || ""}
              onChange={e => handleAxisTitleChange(e, Axis.X_AXIS)}
            />
          </div>
          <div className="flex flex-row gap-2">
              <div className="flex flex-column">
                <label htmlFor="minValue">{T("load-conf.min-value")}</label>
                <InputNumber
                  id="minValue"
                  value={currentConfig.xAxis?.minValue || 0}
                  onValueChange={e => handleAxisMinMaxValueChange(e, "minValue", true)}
                />
              </div>
              <div className="flex flex-column">
                <label htmlFor="maxValue">{T("load-conf.max-value")}</label>
                <InputNumber
                  id="maxValue"
                  value={currentConfig.xAxis?.maxValue || 0}
                  onValueChange={e => handleAxisMinMaxValueChange(e, "maxValue", true)}
                />
              </div>
            </div>
        </div>
      </div>
      <div className="col-12 mb-3 border-1 border-round border-300">
        <div>{T("word.y-axis")}</div>
        <div className="col-12">
          <label htmlFor="dataset">{T("word.dataset")}</label>
          <DatasetDropdown
            value={currentConfig.yAxis?.identifier ?? ""}
            onChange={e => handleDatasetChange(e, Axis.Y_AXIS)}
          />
        </div>
        <div className="flex flex-row justify-content-between">
          <div className="flex flex-row justify-content-start gap-3">
            <div className="col-4 flex flex-column">
              <label htmlFor="title">{T("word.title")}</label>
              <InputText
                id="title"
                type="text"
                value={currentConfig.yAxis?.title || ""}
                onChange={e => handleAxisTitleChange(e, Axis.Y_AXIS)}
              />
            </div>
            <div className="flex flex-column gap-2">
              <div className="flex flex-column gap-2">
                <label htmlFor="minValue">{T("load-conf.min-value")}</label>
                <InputNumber
                  id="minValue"
                  value={currentConfig.yAxis?.minValue || 0}
                  onValueChange={e => handleAxisMinMaxValueChange(e, "minValue", false)}
                />
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="maxValue">{T("load-conf.max-value")}</label>
                <InputNumber
                  id="maxValue"
                  value={currentConfig.yAxis?.maxValue || 0}
                  onValueChange={e => handleAxisMinMaxValueChange(e, "maxValue", false)}
                />
              </div>
            </div>
            <div className="flex flex-column gap-2">
              <div className="flex flex-column gap-2">
                <label htmlFor="minColor">Min {T("word.color")}</label>
                <div className="flex gap-1 align-items-center">
                  <ColorPicker
                    value={currentConfig.yAxis?.minColor ?? defaultMinColor}
                    onValueChange={e => handleColorChange(ColorKey.MIN_COLOR, e.target.value, 1)}
                  />
                  <i
                    className="pi pi-refresh ml-2 cursor-pointer"
                    onClick={() => handleResetColor(ColorKey.MIN_COLOR)}
                  />
                </div>
              </div>
              <div className="flex flex-column gap-2">
                <label htmlFor="maxColor">Max {T("word.color")}</label>
                <div className="flex gap-1 align-items-center">
                  <ColorPicker
                    value={currentConfig.yAxis?.maxColor ?? defaultMaxColor}
                    onValueChange={e => handleColorChange(ColorKey.MAX_COLOR, e.target.value, 1)}
                  />
                  <i
                    className="pi pi-refresh ml-2 cursor-pointer"
                    onClick={() => handleResetColor(ColorKey.MAX_COLOR)}
                  />
                </div>
              </div>
            </div>
          </div>
          {!currentConfig.yAxisSecondary && (
            <Button
              label={T("action.add-secondary-axis")}
              icon="pi pi-plus"
              className="p-button-text p-button-success p-button-text p-ml-2 h-2rem align-self-center"
              onClick={handleAddSecondaryAxis}
            />
          )}
        </div>
      </div>
      {currentConfig.yAxisSecondary && (
        <div className="col-12 mb-3 border-1 border-round border-300">
          <div>{T("label.grid-heatmap.secondary-y-axis")}</div>
          <div className="col-12">
            <label htmlFor="dataset">{T("word.dataset")}</label>
            <DatasetDropdown
              value={currentConfig.yAxisSecondary?.identifier ?? ""}
              onChange={e => handleDatasetChange(e, Axis.Y_AXIS, 2)}
            />
          </div>
          <div className="flex flex-row justify-content-between">
            <div className="flex flex-row justify-content-start gap-3">
              <div className="col-4 flex flex-column">
                <label htmlFor="title">{T("word.title")}</label>
                <InputText
                  id="title"
                  type="text"
                  value={currentConfig.yAxisSecondary?.title || ""}
                  onChange={e => handleAxisTitleChange(e, Axis.Y_AXIS, 2)}
                />
              </div>
              <div className="flex flex-column gap-2">
                <div className="flex flex-column gap-2">
                  <label htmlFor="minValue">{T("load-conf.min-value")}</label>
                  <InputNumber
                    id="minValue"
                    value={currentConfig.yAxisSecondary?.minValue || 0}
                    onValueChange={e => handleAxisMinMaxValueChange(e, "minValue", false, 2)}
                  />
                </div>
                <div className="flex flex-column gap-2">
                  <label htmlFor="maxValue">{T("load-conf.max-value")}</label>
                  <InputNumber
                    id="maxValue"
                    value={currentConfig.yAxisSecondary?.maxValue || 0}
                    onValueChange={e => handleAxisMinMaxValueChange(e, "maxValue", false, 2)}
                  />
                </div>
              </div>
              <div className="flex flex-column gap-3">
                <div className="flex flex-column gap-2">
                  <label htmlFor="minColor">Min {T("word.color")}</label>
                  <div className="flex gap-1 align-items-center">
                    <ColorPicker
                      value={currentConfig.yAxisSecondary?.minColor ?? defaultMinColor}
                      onValueChange={e => handleColorChange(ColorKey.MIN_COLOR, e.target.value, 2)}
                    />
                    <i
                      className="pi pi-refresh ml-2 cursor-pointer"
                      onClick={() => handleResetColor(ColorKey.MIN_COLOR)}
                    />
                  </div>
                </div>
                <div className="flex flex-column gap-2">
                  <label htmlFor="maxColor">Max {T("word.color")}</label>
                  <div className="flex gap-1 align-items-center">
                    <ColorPicker
                      value={currentConfig.yAxisSecondary?.maxColor ?? defaultMaxColor}
                      onValueChange={e => handleColorChange(ColorKey.MAX_COLOR, e.target.value, 2)}
                    />
                    <i
                      className="pi pi-refresh ml-2 cursor-pointer"
                      onClick={() => handleResetColor(ColorKey.MAX_COLOR)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <Button
              label={T("action.remove-secondary-axis")}
              icon="pi pi-minus"
              className="p-button-text p-button-danger p-button-text p-ml-2 h-2rem align-self-center"
              onClick={handleRemoveSecondaryAxis}
            />
          </div>
        </div>
      )}
      <div className="flex flex-row flex-wrap justify-content-between">
        {currentConfig.isLoadConfig && currentConfig.loadConf && (
          <LoadConfigInputs
            loadConf={currentConfig.loadConf}
            handleLoadConfChange={handleLoadConfChange}
          />
        )}
      </div>
    </div>
  );
};

export default GridHeatmapEditor;
